import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataServiceBase } from '../../../core/service/base/data.service.base';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { LeaseAgreementInformationRow } from '../../model/lease-agreement-information-row';

export interface LeaseAgreementInformationRowsChange {
  leaseAgreementId: string;
  rows: LeaseAgreementInformationRow[];
}

@Injectable({ providedIn: 'root' })
export class LeaseAgreementInformationRowService extends DataServiceBase {
  private readonly _rowsChanged = new Subject<LeaseAgreementInformationRowsChange>();
  readonly rowsChanged$ = this._rowsChanged.asObservable();

  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, messageService, progressBar, config, 'lease-agreement-information-row');
  }

  getRows(leaseAgreementId: string): Observable<LeaseAgreementInformationRow[]> {
    return this.request(
      leaseAgreementId,
      this.client.get<LeaseAgreementInformationRow[]>(`${this.endpoint}/${leaseAgreementId}`)
    );
  }

  createRow(leaseAgreementId: string, rowModel: LeaseAgreementInformationRow): Observable<LeaseAgreementInformationRow> {
    this.progressBar.start();
    return this.client
      .post<LeaseAgreementInformationRow>(this.endpoint, {
        entityId: leaseAgreementId,
        rowModel,
      })
      .pipe(
        map(row => new LeaseAgreementInformationRow(row)),
        tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_INFORMATION_ROW_SAVED')),
        catchError(error => {
          this.notification.showError(error);
          return throwError(() => error);
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  updateRow(leaseAgreementId: string, rowModel: LeaseAgreementInformationRow) {
    return this.request(
      leaseAgreementId,
      this.client.put<LeaseAgreementInformationRow[]>(this.endpoint, {
        entityId: leaseAgreementId,
        rowModel,
      }),
      'SUCCESS.LEASE_AGREEMENT_INFORMATION_ROW_SAVED'
    );
  }

  deleteRow(leaseAgreementId: string, rowId: string) {
    return this.request(
      leaseAgreementId,
      this.client.delete<LeaseAgreementInformationRow[]>(`${this.endpoint}/${leaseAgreementId}/${rowId}`),
      'SUCCESS.LEASE_AGREEMENT_INFORMATION_ROW_REMOVED'
    );
  }

  reorderRows(leaseAgreementId: string, rowIds: string[]) {
    return this.request(
      leaseAgreementId,
      this.client.put<LeaseAgreementInformationRow[]>(`${this.endpoint}/reorder-rows`, {
        entityId: leaseAgreementId,
        rowIds,
      })
    );
  }

  private request(
    leaseAgreementId: string,
    request: Observable<LeaseAgreementInformationRow[]>,
    successMessage?: string
  ): Observable<LeaseAgreementInformationRow[]> {
    this.progressBar.start();
    return request.pipe(
      map(rows => rows.map(row => new LeaseAgreementInformationRow(row))),
      tap(rows => {
        this._rowsChanged.next({ leaseAgreementId, rows });
        if (successMessage) this.notification.showSuccess(successMessage);
      }),
      catchError(error => {
        this.notification.showError(error);
        return throwError(() => error);
      }),
      finalize(() => this.progressBar.stop())
    );
  }
}
